import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { ArrowRight, ArrowUpRight, Clock, Search } from "lucide-react";
import axios from "axios";
import Seo from "@/components/Seo";
import { ARTICLES } from "@/data/blogArticles";
import { BucketGrid } from "@/components/BlogSection";
import { FadeUp, Eyebrow, scrollToId } from "@/lib/motion";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BlogPage() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [posts, setPosts] = useState(null);
  const [error, setError] = useState("");
  const [query, setQuery] = useState("");

  useEffect(() => {
    axios
      .get(`${API}/blog`)
      .then(({ data }) => setPosts(data.posts || []))
      .catch(() => setError("Couldn't load the posts. Please refresh."));
  }, []);

  const q = query.trim().toLowerCase();
  const matches = (text) => !q || String(text).toLowerCase().includes(q);
  const guides = ARTICLES.filter(
    (a) =>
      matches(a.title) ||
      matches(a.excerpt) ||
      matches(a.description) ||
      matches(a.category)
  );
  const tipPosts = (posts || []).filter(
    (p) => matches(p.title) || matches(p.excerpt)
  );

  const goBook = (e) => {
    if (e) e.preventDefault();
    if (pathname !== "/") {
      navigate("/");
      setTimeout(() => scrollToId("book"), 600);
    setTimeout(() => scrollToId("book"), 1600);
    } else {
      scrollToId("book");
    }
  };

  return (
    <main data-testid="blog-page">
      <Seo
        title="Blog & Resources — ZD Design"
        description="Todd Meisler's short, practical SEO tips — search, remarketing and website advice from 30+ years in the field. The same guidance he gives his clients."
        path="/blog"
      />

      <section className="grain relative overflow-hidden pb-14 pt-40 md:pb-20 md:pt-48">
        <div className="pointer-events-none absolute -right-40 top-0 h-[420px] w-[420px] rounded-full bg-flame/10 blur-[140px]" />
        <div className="relative mx-auto grid max-w-7xl items-center gap-10 px-4 sm:px-8 lg:grid-cols-[1.3fr_1fr] lg:gap-14 lg:px-12">
          <FadeUp>
            <Eyebrow>Blog &amp; resources</Eyebrow>
            <h1 className="mt-5 max-w-3xl font-display text-4xl font-extrabold leading-[0.98] tracking-tight text-slate-900 sm:text-5xl md:text-6xl">
              The ZD Design Blog — short, useful,{" "}
              <span className="text-flame">no jargon.</span>
            </h1>
            <p className="mt-7 max-w-2xl text-lg leading-relaxed text-mist md:text-xl">
              Todd's notes on search, remarketing and websites — the same
              advice he gives clients, written down.
            </p>
          </FadeUp>
          <FadeUp delay={0.1}>
            <img
              src="/img/blog/blog-roll-writer.webp"
              alt="Ink line drawing of a writer smiling while typing at her laptop"
              data-testid="blog-hero-art"
              className="w-full select-none rounded-tr-[2.5rem]"
            />
          </FadeUp>
        </div>
      </section>

      <section className="border-t border-slate-200 py-20 md:py-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-12">
          <FadeUp>
            <Eyebrow>Resources &amp; guides</Eyebrow>
            <h2 className="mt-5 max-w-2xl font-display text-3xl font-bold leading-[1.02] tracking-tight text-slate-900 sm:text-4xl">
              Start here<span className="text-flame">.</span>
            </h2>
            <div className="mt-8 max-w-xl">
              <label
                htmlFor="blog-search"
                className="font-mono text-[13px] uppercase tracking-[0.2em] text-faint"
              >
                Search the blog
              </label>
              <div className="relative mt-3">
                <Search className="pointer-events-none absolute left-5 top-1/2 h-5 w-5 -translate-y-1/2 text-faint" />
                <input
                  id="blog-search"
                  data-testid="blog-search-input"
                  type="search"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Try “reviews”, “SEO” or “ads”"
                  className="w-full rounded-full border border-slate-300 bg-white py-3.5 pl-12 pr-5 text-base text-slate-900 outline-none transition-colors placeholder:text-faint focus:border-flame"
                />
              </div>
              <p
                data-testid="blog-search-count"
                className="mt-3 font-mono text-[13px] uppercase tracking-[0.15em] text-faint"
              >
                {guides.length} guide{guides.length === 1 ? "" : "s"}{" "}
                {q ? "found" : "available"}
              </p>
            </div>
          </FadeUp>
          {q && guides.length === 0 ? (
            <p
              data-testid="blog-search-empty"
              className="mt-10 rounded-xl border border-slate-200 bg-white px-5 py-6 text-base text-mist"
            >
              No guides match “{query}” — try a different word.
            </p>
          ) : (
            <div className="mt-10">
              <BucketGrid articles={guides} />
            </div>
          )}
        </div>
      </section>

      <section className="border-t border-slate-200 py-20 md:py-24">
        <div className="mx-auto max-w-4xl px-4 sm:px-8 lg:px-12">
          {error && (
            <p data-testid="blog-error" className="text-base text-red-300">
              {error}
            </p>
          )}
          {posts === null && !error && (
            <p data-testid="blog-loading" className="text-base text-faint">
              Loading…
            </p>
          )}
          {posts !== null && posts.length === 0 && !error && !q && (
            <p
              data-testid="blog-empty"
              className="rounded-xl border border-slate-200 bg-coal/60 px-5 py-6 text-base text-mist"
            >
              No posts yet — Todd's tips are on the way. Check back soon.
            </p>
          )}
          {posts !== null && q && tipPosts.length === 0 && (
            <p
              data-testid="blog-tips-empty"
              className="rounded-xl border border-slate-200 bg-coal/60 px-5 py-6 text-base text-mist"
            >
              No tips match “{query}” — try a different word.
            </p>
          )}
          <div className="space-y-4">
            {tipPosts.map((p, i) => (
              <FadeUp key={p.slug} delay={Math.min(i * 0.06, 0.3)}>
                <Link
                  to={`/blog/${p.slug}`}
                  data-testid={`blog-post-link-${p.slug}`}
                  className="group flex items-start justify-between gap-6 rounded-2xl border border-slate-200 bg-coal/70 p-7 transition-all duration-300 hover:-translate-y-0.5 hover:border-flame/40"
                >
                  <div>
                    <div className="font-mono text-[13px] uppercase tracking-[0.2em] text-faint">
                      {String(p.created_at || "").slice(0, 10)}
                    </div>
                    <h2 className="mt-2 font-display text-xl font-bold tracking-tight text-slate-900 sm:text-2xl">
                      {p.title}
                    </h2>
                    <p className="mt-3 text-base leading-relaxed text-mist">
                      {p.excerpt}
                    </p>
                  </div>
                  <ArrowRight className="mt-1 h-5 w-5 shrink-0 text-flame transition-transform duration-300 group-hover:translate-x-1" />
                </Link>
              </FadeUp>
            ))}
          </div>

          <FadeUp delay={0.1}>
            <div className="mt-14 rounded-2xl border border-slate-200 bg-obsidian p-10 text-center md:p-14">
              <h2 className="mx-auto max-w-2xl font-display text-3xl font-bold leading-[1.05] tracking-tight text-slate-900 sm:text-4xl">
                Want tips applied to your site?
              </h2>
              <p className="mx-auto mt-5 max-w-md text-lg leading-relaxed text-mist">
                Book a free strategy call — pick a time that suits you and
                talk directly with Todd.
              </p>
              <button
                data-testid="blog-cta-book"
                onClick={goBook}
                className="group mx-auto mt-8 flex items-center gap-2 rounded-2xl bg-flame px-7 py-4 font-mono text-base font-semibold uppercase tracking-[0.15em] text-obsidian transition-all duration-300 hover:shadow-[0_0_40px_rgba(255,85,0,0.5)]"
              >
                Book a free strategy call
                <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </button>
            </div>
          </FadeUp>
        </div>
      </section>
    </main>
  );
}
